import * as React from 'react'
import { c, msgid, ngettext, t } from 'ttag'
import { ReactComponent as MinutesIcon } from '../../assets/v2/icons/icon-activity-minutes.svg'
import { ReactComponent as StepsIcon } from '../../assets/v2/icons/icon-activity-steps.svg'
import { ReactComponent as CaloriesIcon } from '../../assets/v2/icons/icon-activity-calories.svg'
import { formatThousands } from '../../common/helpers/translations'
import { ActivityProgress, CustomType, ProgressType, UserDailyProgress, UserId } from '../../services/api.types'
import './YumUserProgress.scss'
import moment from 'moment'
import { getPersistenceDailyTotal, isCollectiveType, isPersistenceType } from '../../common/helpers/challenges'
import { convertDateToTallinnTimeZone } from '../../common/helpers/date'
import { unitsDisplayDurationCalorieLong, unitsDisplayDurationMinuteLong } from '../../common/helpers/units'
import { isRtl } from '../../i18nInit'
import { customTypes, customUnits, getCustomTypeText } from '../../services/CustomTypes'

export interface IUserProgressProps {
  activityProgress: ActivityProgress
  progressType: ProgressType
  activityFormulaTarget: number | null
  collectiveGoal: number | null
  collectiveTotal: number
  collectivePercentage: number
  ownerUserId: UserId
  participantsCount: number
  dailyPersistenceGoal: number
  startDate: number
  endDate: number
  progressResults: UserDailyProgress[]
  customType: CustomType | null
  className?: string
}

interface IUserProgressPropsCombined extends IUserProgressProps {
  getProgress(payload?: { startDate?: Date; endDate?: Date }): void
}

interface IUserProgressState {
  showTooltip: GOAL_PARAMETER | null
}

enum GOAL_PARAMETER {
  STEPS = 'steps',
  INTENSIVE = 'intensive',
  CALORIES = 'calories',
  FORMULA = 'formula',
  COLLECTIVE_STEPS = 'collective_steps',
  COLLECTIVE_STEPS_ME = 'collective_steps_me',
  COLLECTIVE_STEPS_AVG = 'collective_steps_avg',
  COLLECTIVE_CALORIES = 'collective_calories',
  COLLECTIVE_CALORIES_ME = 'collective_calories_me',
  COLLECTIVE_CALORIES_AVG = 'collective_calories_avg',
  COLLECTIVE_MINUTES = 'collective_minutes',
  COLLECTIVE_MINUTES_ME = 'collective_minutes_me',
  COLLECTIVE_MINUTES_AVG = 'collective_minutes_avg',
  COLLECTIVE_CUSTOM = 'collective_custom',
  COLLECTIVE_CUSTOM_ME = 'collective_custom_me',
  COLLECTIVE_CUSTOM_AVG = 'collective_custom_avg',
  PERSISTENCE_STEPS = 'persistence_steps',
  PERSISTENCE_CALORIES = 'persistence_calories',
  PERSISTENCE_MINUTES = 'persistence_minutes',
  PERSISTENCE_ACTIVITY_INDEX = 'persistence_activity_index',
  PERSISTENCE_PROGRESS = 'persistence_progress',
  PERSISTENCE_CALENDAR = 'persistence_calendar',
}

export class YumUserProgress extends React.PureComponent<IUserProgressPropsCombined, IUserProgressState> {
  state = { showTooltip: null }
  tooltipTimer: any

  updateProgressData() {
    const isPersistenceChallenge = isPersistenceType(this.props.progressType)
    if (isPersistenceChallenge) {
      const { getProgress, startDate: startDateOrig, endDate: endDateOrig } = this.props
      const startDate = convertDateToTallinnTimeZone(new Date(startDateOrig))
      const endDate = convertDateToTallinnTimeZone(new Date(endDateOrig))
      getProgress({ startDate, endDate })
    }
  }

  componentDidMount() {
    this.updateProgressData()
  }

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<IUserProgressPropsCombined>) {
    if (this.props.activityProgress?.lastSync !== nextProps.activityProgress?.lastSync) {
      console.log('LAST SYNC CHANGE!')
      this.updateProgressData()
    }
  }

  /*componentWillReceiveProps(nextProps: { [x: string]: any }) {
    Object.keys(nextProps)
      .filter(key => {
        return nextProps[key] !== this.props[key];
      })
  }*/

  BEM = () => {
    const { className } = this.props
    const classArray = ['yum-user-progress']

    if (className) {
      classArray.push(className)
    }

    return classArray.join(' ')
  }

  itemWrapperBEM = (parameter: GOAL_PARAMETER, longWidth: boolean) => {
    const { progressType } = this.props
    const classArray = ['yum-user-progress__item-wrapper']

    if (
      (progressType === ProgressType.STEPS && parameter === GOAL_PARAMETER.STEPS) ||
      (parameter !== GOAL_PARAMETER.PERSISTENCE_CALENDAR && longWidth)
    ) {
      classArray.push('yum-user-progress__item-wrapper--100')
    }

    if (
      parameter === GOAL_PARAMETER.COLLECTIVE_STEPS ||
      parameter === GOAL_PARAMETER.COLLECTIVE_CALORIES ||
      parameter === GOAL_PARAMETER.COLLECTIVE_MINUTES ||
      parameter === GOAL_PARAMETER.COLLECTIVE_CUSTOM ||
      parameter === GOAL_PARAMETER.PERSISTENCE_CALENDAR
    ) {
      if (longWidth) {
        classArray.push('yum-user-progress__item-wrapper--100-75')
      } else if (!this.props.activityProgress) {
        classArray.push('yum-user-progress__item-wrapper--100')
      } else {
        classArray.push('yum-user-progress__item-wrapper--100-50')
      }
    }

    if (
      [
        GOAL_PARAMETER.COLLECTIVE_STEPS_ME,
        GOAL_PARAMETER.COLLECTIVE_STEPS_AVG,
        GOAL_PARAMETER.COLLECTIVE_CALORIES_ME,
        GOAL_PARAMETER.COLLECTIVE_CALORIES_AVG,
        GOAL_PARAMETER.COLLECTIVE_MINUTES_ME,
        GOAL_PARAMETER.COLLECTIVE_MINUTES_AVG,
      ].includes(parameter)
    ) {
      classArray.push('yum-user-progress__item-wrapper--50-25')
    }

    return classArray.join(' ')
  }

  itemBEM = (parameter: GOAL_PARAMETER, progress?: number) => {
    const { progressType } = this.props
    const classArray = ['yum-user-progress__item', parameter]

    if (
      (progressType !== ProgressType.STEPS && parameter === GOAL_PARAMETER.FORMULA) ||
      parameter === GOAL_PARAMETER.COLLECTIVE_STEPS ||
      parameter === GOAL_PARAMETER.COLLECTIVE_CALORIES ||
      parameter === GOAL_PARAMETER.COLLECTIVE_MINUTES ||
      parameter === GOAL_PARAMETER.COLLECTIVE_CUSTOM ||
      parameter === GOAL_PARAMETER.PERSISTENCE_PROGRESS
    ) {
      classArray.push('yum-user-progress__item--special')
    }

    if (progressType === ProgressType.STEPS && parameter === GOAL_PARAMETER.STEPS) {
      classArray.push('yum-user-progress__item--special')
    }

    if (progress && progress >= 100) {
      classArray.push('yum-user-progress__item--completed')
    }

    if (progressType === ProgressType.STEPS) {
      if (parameter !== GOAL_PARAMETER.STEPS) {
        classArray.push('yum-user-progress__item--disabled')
      } else {
        classArray.push('yum-user-progress__item--lg')
      }
    }

    return classArray.join(' ')
  }

  dayBEM = (date: string, met: boolean) => {
    const base = 'yum-user-progress__day'
    const classArray = [base]
    const isToday = moment().locale('en').format('YYYY-MM-DD') === date
    const isPast = moment(date).isBefore(new Date())

    if (isToday || isPast) {
      classArray.push(`${base}--${met ? 'met' : 'unmet'}`)
    }

    if (isToday) {
      classArray.push(`${base}--current`)
    }

    return classArray.join(' ')
  }

  getGoalLabel(parameter: GOAL_PARAMETER) {
    const { customType } = this.props
    switch (parameter) {
      case GOAL_PARAMETER.CALORIES:
        return unitsDisplayDurationCalorieLong
      case GOAL_PARAMETER.INTENSIVE:
        return unitsDisplayDurationMinuteLong
      case GOAL_PARAMETER.STEPS:
        return t`Steps`
      case GOAL_PARAMETER.FORMULA:
        return t`Activity index`
      case GOAL_PARAMETER.COLLECTIVE_STEPS:
        return t`Collective steps`
      case GOAL_PARAMETER.COLLECTIVE_CUSTOM:
        return t`Collective goal` + ' (' + getCustomTypeText(customType) + ')'
      case GOAL_PARAMETER.COLLECTIVE_STEPS_ME:
      case GOAL_PARAMETER.COLLECTIVE_CALORIES_ME:
      case GOAL_PARAMETER.COLLECTIVE_MINUTES_ME:
      case GOAL_PARAMETER.COLLECTIVE_CUSTOM_ME:
        return t`Your contribution`
      case GOAL_PARAMETER.COLLECTIVE_STEPS_AVG:
      case GOAL_PARAMETER.COLLECTIVE_CALORIES_AVG:
      case GOAL_PARAMETER.COLLECTIVE_MINUTES_AVG:
      case GOAL_PARAMETER.COLLECTIVE_CUSTOM_AVG:
        return t`Average contribution`
      case GOAL_PARAMETER.COLLECTIVE_CALORIES:
        return t`Collective calories`
      case GOAL_PARAMETER.COLLECTIVE_MINUTES:
        return t`Collective minutes`
      case GOAL_PARAMETER.PERSISTENCE_STEPS:
      case GOAL_PARAMETER.PERSISTENCE_CALORIES:
      case GOAL_PARAMETER.PERSISTENCE_MINUTES:
      case GOAL_PARAMETER.PERSISTENCE_ACTIVITY_INDEX:
        return t`Daily target`
      case GOAL_PARAMETER.PERSISTENCE_PROGRESS:
        return t`Total progress`
      default:
        return `Unknown`
    }
  }

  getIcon(parameter: GOAL_PARAMETER) {
    return [
      GOAL_PARAMETER.CALORIES,
      GOAL_PARAMETER.COLLECTIVE_CALORIES,
      GOAL_PARAMETER.COLLECTIVE_CALORIES_ME,
      GOAL_PARAMETER.COLLECTIVE_CALORIES_AVG,
      GOAL_PARAMETER.PERSISTENCE_CALORIES,
    ].includes(parameter)
      ? CaloriesIcon
      : [
          GOAL_PARAMETER.STEPS,
          GOAL_PARAMETER.COLLECTIVE_STEPS,
          GOAL_PARAMETER.COLLECTIVE_STEPS_ME,
          GOAL_PARAMETER.COLLECTIVE_STEPS_AVG,
          GOAL_PARAMETER.PERSISTENCE_STEPS,
        ].includes(parameter)
      ? StepsIcon
      : MinutesIcon
  }

  getGoalBigText(parameter: GOAL_PARAMETER, total: number, unit?: string) {
    const Icon = this.getIcon(parameter)
    return (
      <div
        className={`yum-user-progress__big-text ${
          [
            GOAL_PARAMETER.COLLECTIVE_STEPS_ME,
            GOAL_PARAMETER.COLLECTIVE_CALORIES_ME,
            GOAL_PARAMETER.COLLECTIVE_MINUTES_ME,
          ].includes(parameter)
            ? 'yum-user-progress__big-text--blue'
            : ''
        }`}
      >
        {formatThousands(total)}{' '}
        {[GOAL_PARAMETER.COLLECTIVE_CUSTOM_ME, GOAL_PARAMETER.COLLECTIVE_CUSTOM_AVG].includes(parameter) ? (
          unit
        ) : (
          <Icon></Icon>
        )}
      </div>
    )
  }

  getGoalText(parameter: GOAL_PARAMETER, total: number, goal: number, unit?: string) {
    const Icon = this.getIcon(parameter)

    let goalNumber = formatThousands(goal) ? formatThousands(goal) : '0'
    if (parameter === GOAL_PARAMETER.STEPS || parameter === GOAL_PARAMETER.CALORIES) {
      goalNumber = `${Math.floor(goal / 1000)}K`
    }

    let totalNumber = formatThousands(total) ? formatThousands(total) : '0'
    let totalNumberCondensed = formatThousands(total) ? formatThousands(total) : '0'
    if ((parameter === GOAL_PARAMETER.STEPS || parameter === GOAL_PARAMETER.CALORIES) && total > 999) {
      totalNumberCondensed = `${Math.floor(total / 1000)}K`
      if (total > 999999) {
        totalNumber = formatThousands(total) ? `${formatThousands(Math.floor(total / 1000))}K` : '0'
      }
    }

    if (parameter === GOAL_PARAMETER.FORMULA || parameter === GOAL_PARAMETER.PERSISTENCE_ACTIVITY_INDEX) {
      return (
        <div className={'yum-user-progress__text-formula'}>
          <span className={'hide-on-larger-phones'}>
            {c('short').t`AVG`}(<MinutesIcon style={{ margin: '0 2px' }} />,<StepsIcon style={{ margin: '0 2px' }} />,
            <CaloriesIcon />)
          </span>
          <span className={'hide-on-small-phone'}>
            {c('short').t`AVG`} (<MinutesIcon style={isRtl ? { marginRight: 3 } : { marginLeft: 3 }} /> , <StepsIcon />{' '}
            , <CaloriesIcon />)
          </span>
        </div>
      )
    } else {
      const daysNumber = parameter === GOAL_PARAMETER.PERSISTENCE_PROGRESS ? parseInt(totalNumber as string) : 0
      return (
        <>
          {parameter == GOAL_PARAMETER.PERSISTENCE_PROGRESS ? (
            <>
              <span className={'hide-on-larger-phones'}>
                {daysNumber}
                <span className={'yum-user-progress__text_goal_50'}>
                  {`/${goalNumber} `}
                  {ngettext(msgid`day`, `days`, goal)}
                </span>
              </span>
              <span className={'hide-on-small-phone'}>
                {daysNumber}{' '}
                <span className={'yum-user-progress__text_goal_50'}>
                  {` / ${goalNumber} `}
                  {ngettext(msgid`day`, `days`, goal)}
                </span>
              </span>
            </>
          ) : (
            <>
              {parameter !== GOAL_PARAMETER.COLLECTIVE_CUSTOM && (
                <Icon className={`yum-user-progress__icon yum-user-progress__icon--${parameter}`} />
              )}
              <span
                className={'hide-on-larger-phones'}
                style={
                  parameter === GOAL_PARAMETER.COLLECTIVE_CUSTOM ? {} : isRtl ? { marginRight: 1 } : { marginLeft: 1 }
                }
              >
                {totalNumberCondensed}
                <span className={'yum-user-progress__text_goal'}>
                  {`/${goalNumber}`} {parameter === GOAL_PARAMETER.COLLECTIVE_CUSTOM && unit}
                </span>
              </span>
              <span
                className={'hide-on-small-phone'}
                style={
                  parameter === GOAL_PARAMETER.COLLECTIVE_CUSTOM ? {} : isRtl ? { marginRight: 3 } : { marginLeft: 3 }
                }
              >
                {totalNumber}{' '}
                <span className={'yum-user-progress__text_goal'}>
                  {` / ${goalNumber}`} {parameter === GOAL_PARAMETER.COLLECTIVE_CUSTOM && unit}
                </span>
              </span>
            </>
          )}
        </>
      )
    }
  }

  handleOpenTooltip = (parameter: GOAL_PARAMETER) => (event: { type: string }) => {
    const showTooltip = event.type === 'mouseover' || this.state.showTooltip !== parameter ? parameter : null
    this.setState({ showTooltip })
  }

  handleCloseTooltip = (parameter: GOAL_PARAMETER) => () => {
    if (this.state.showTooltip === parameter) {
      this.setState({ showTooltip: null })
    }
  }

  renderGoalGraph = ({
    parameter,
    percentage,
    progress,
    total,
    goal,
    tooltip,
    mobileOnly,
    desktopOnly,
    longWidth = false,
    unit,
  }: {
    parameter: GOAL_PARAMETER
    percentage?: number
    progress?: number
    total: number
    goal?: number
    tooltip?: string
    mobileOnly?: boolean
    desktopOnly?: boolean
    longWidth?: boolean
    unit?: string
  }) => {
    const isPersistenceProgress = parameter === GOAL_PARAMETER.PERSISTENCE_PROGRESS
    let classes = this.itemWrapperBEM(parameter, longWidth)
    if (mobileOnly) classes += ' yum-user-progress__item-wrapper--mobile'
    if (desktopOnly) classes += ' yum-user-progress__item-wrapper--desktop'
    if (percentage === null) percentage = 0
    return (
      /*<BlueTooltip
        title={tooltip}
        placement="top"
        PopperProps={{
          container: document.getElementById('content'),
        }}
        arrow
        enterTouchDelay={50}
        leaveTouchDelay={3000}
        open={this.state.showTooltip === parameter}
        onOpen={this.handleOpenTooltip(parameter)}
        onClose={this.handleCloseTooltip(parameter)}
      ></BlueTooltip>*/
      <div className={classes}>
        <div className={this.itemBEM(parameter, progress)}>
          <div className="yum-user-progress__label">{this.getGoalLabel(parameter)}</div>
          {goal !== undefined && (
            <div className="yum-user-progress__text">{this.getGoalText(parameter, total, goal, unit)}</div>
          )}
          {parameter !== GOAL_PARAMETER.CALORIES &&
            goal == undefined &&
            total !== undefined &&
            this.getGoalBigText(parameter, total, unit)}
          {progress !== undefined && (
            <div className="yum-user-progress__progress">
              <div
                className="yum-user-progress__progressbar"
                style={{ width: `${progress > 100 ? 100 : progress}%` }}
              />
            </div>
          )}
          {percentage !== undefined && (
            <div className="yum-user-progress__percentage">
              {percentage}
              <small>%</small>
            </div>
          )}
          {/*{parameter !== GOAL_PARAMETER.FORMULA ? (
            <ActivityDataInfoIcon className={'yum-user-progress__info-icon'} />
          ) : (
            <ActivityFormulaInfoIcon className={'yum-user-progress__info-icon'} />
          )}*/}
        </div>
      </div>
    )
  }

  renderPersistenceCalendar = ({
    progressResults,
    startDate,
    endDate,
    dailyPersistenceGoal,
    daysNumber,
  }: {
    progressResults: UserDailyProgress[]
    startDate: number
    endDate: number
    dailyPersistenceGoal: number
    daysNumber: number
  }) => {
    const parameter = GOAL_PARAMETER.PERSISTENCE_CALENDAR
    const now = new Date().getTime()
    const isActiveGoal = startDate < now && endDate > now
    return (
      <div className={this.itemWrapperBEM(parameter, !isActiveGoal)}>
        <div className={this.itemBEM(parameter, 50)}>
          <div className="yum-user-progress__label">{t`${daysNumber} day challenge`}</div>

          <div className={'yum-user-progress__days'}>
            {progressResults.length == daysNumber &&
              (progressResults as UserDailyProgress[]).map((userDailyProgress, keyIndex) => {
                const total = getPersistenceDailyTotal(userDailyProgress, this.props.progressType, true)
                const met = total >= dailyPersistenceGoal
                const { date } = userDailyProgress
                return <div title={date} className={this.dayBEM(date, met)} key={keyIndex} />
              })}
          </div>
        </div>
      </div>
    )
  }

  render() {
    const {
      progressType,
      activityFormulaTarget,
      collectiveGoal: collectiveGoalRaw,
      collectiveTotal,
      collectivePercentage,
      participantsCount,
      dailyPersistenceGoal,
      startDate,
      endDate,
      progressResults,
      customType,
    } = this.props

    let isUser = true
    const isCollectiveChallenge = isCollectiveType(progressType)
    const isPersistenceChallenge = isPersistenceType(progressType)

    if (!this.props.activityProgress) {
      if (!isCollectiveChallenge) {
        return ''
      }
      isUser = false
    }

    const {
      intensiveMinutesGoal,
      intensiveMinutesTotal,
      intensiveMinutesPercentage,
      stepsGoal,
      stepsTotal,
      stepsPercentage,
      caloriesGoal,
      caloriesTotal,
      caloriesPercentage,
      totalPercentage,
      persistenceGoal: persistenceGoalRaw,
      persistencePercentage,
      persistenceTotal,
    } = this.props.activityProgress || {
      intensiveMinutesGoal: 0,
      intensiveMinutesTotal: 0,
      intensiveMinutesPercentage: 0,
      stepsGoal: 0,
      stepsTotal: 0,
      stepsPercentage: 0,
      caloriesGoal: 0,
      caloriesTotal: 0,
      caloriesPercentage: 0,
      totalPercentage: 0,
    }
    const persistenceGoal = persistenceGoalRaw || 0
    const collectiveGoal = collectiveGoalRaw || 0

    const formulaPercentage =
      progressType !== ProgressType.STEPS
        ? totalPercentage
        : Math.round((intensiveMinutesPercentage + stepsPercentage + caloriesPercentage) / 3)

    let formulaProgress = formulaPercentage
    let intensiveMinutesProgress = intensiveMinutesPercentage
    let stepsProgress = stepsPercentage
    let caloriesProgress = caloriesPercentage
    if (progressType === ProgressType.ALL && activityFormulaTarget) {
      formulaProgress = Math.round(formulaPercentage * (100 / activityFormulaTarget))
      intensiveMinutesProgress = Math.round(intensiveMinutesPercentage * (100 / activityFormulaTarget))
      stepsProgress = Math.round(stepsPercentage * (100 / activityFormulaTarget))
      caloriesProgress = Math.round(caloriesPercentage * (100 / activityFormulaTarget))
    }
    const collectiveAverage =
      isCollectiveChallenge && participantsCount ? Math.round(collectiveTotal / participantsCount) : 0

    const daysNumber = moment(endDate).diff(moment(startDate), 'days') + 1

    let persistenceTargetTotal = 0
    let persistenceTargetPercentage = 0
    if (isPersistenceChallenge) {
      const today = progressResults.find((day) => day.date === moment().locale('en').format('YYYY-MM-DD'))
      persistenceTargetTotal = today ? getPersistenceDailyTotal(today, progressType, true) : 0
      persistenceTargetPercentage = Math.round((persistenceTargetTotal / dailyPersistenceGoal) * 100)
    }

    const isStepsGoal: boolean = progressType === ProgressType.STEPS
    const isFormulaGoal: boolean = progressType === ProgressType.ALL || progressType === ProgressType.FORMULA

    const now = new Date().getTime()
    const isActiveGoal = startDate < now && endDate > now

    const unit = customUnits.find((cu) => cu.id === customType)?.text

    return (
      progressType && (
        <div className={this.BEM()}>
          {progressType === ProgressType.PERSISTENCE_STEPS &&
            isActiveGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.PERSISTENCE_STEPS,
              percentage: persistenceTargetPercentage,
              progress: persistenceTargetPercentage,
              total: persistenceTargetTotal,
              goal: dailyPersistenceGoal,
            })}
          {progressType === ProgressType.PERSISTENCE_CALORIES &&
            isActiveGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.PERSISTENCE_CALORIES,
              percentage: persistenceTargetPercentage,
              progress: persistenceTargetPercentage,
              total: persistenceTargetTotal,
              goal: dailyPersistenceGoal,
            })}
          {progressType === ProgressType.PERSISTENCE_INTENSIVE_MINUTES &&
            isActiveGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.PERSISTENCE_MINUTES,
              percentage: persistenceTargetPercentage,
              progress: persistenceTargetPercentage,
              total: persistenceTargetTotal,
              goal: dailyPersistenceGoal,
            })}
          {progressType === ProgressType.PERSISTENCE_ALL &&
            isActiveGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.PERSISTENCE_ACTIVITY_INDEX,
              percentage: persistenceTargetTotal,
              progress: persistenceTargetPercentage,
              total: persistenceTargetTotal,
              goal: dailyPersistenceGoal,
            })}
          {isPersistenceChallenge &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.PERSISTENCE_PROGRESS,
              percentage: persistencePercentage,
              progress: persistencePercentage,
              total: persistenceTotal,
              goal: persistenceGoal,
              mobileOnly: true,
              longWidth: !isActiveGoal,
            })}
          {isPersistenceChallenge &&
            this.renderPersistenceCalendar({
              progressResults,
              startDate,
              endDate,
              dailyPersistenceGoal,
              daysNumber,
            })}
          {isPersistenceChallenge &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.PERSISTENCE_PROGRESS,
              percentage: persistencePercentage,
              progress: persistencePercentage,
              total: persistenceTotal,
              goal: persistenceGoal,
              desktopOnly: true,
            })}

          {progressType === ProgressType.COLLECTIVE_STEPS &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_STEPS,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              mobileOnly: true,
            })}
          {progressType === ProgressType.COLLECTIVE_CALORIES &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_CALORIES,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              mobileOnly: true,
            })}
          {progressType === ProgressType.COLLECTIVE_INTENSIVE_MINUTES &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_MINUTES,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              mobileOnly: true,
            })}
          {progressType === ProgressType.COLLECTIVE_CUSTOM &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_CUSTOM,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              mobileOnly: true,
              unit,
            })}

          {isUser && progressType === ProgressType.COLLECTIVE_STEPS && (
            <>
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_STEPS_ME,
                total: stepsTotal,
              })}
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_STEPS_AVG,
                total: collectiveAverage,
              })}
            </>
          )}
          {isUser && progressType === ProgressType.COLLECTIVE_CALORIES && (
            <>
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_CALORIES_ME,
                total: caloriesTotal,
              })}
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_CALORIES_AVG,
                total: collectiveAverage,
              })}
            </>
          )}
          {!isUser && progressType === ProgressType.COLLECTIVE_CALORIES && (
            <>
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_CALORIES_AVG,
                total: collectiveAverage,
                longWidth: true,
              })}
            </>
          )}
          {isUser && progressType === ProgressType.COLLECTIVE_INTENSIVE_MINUTES && (
            <>
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_MINUTES_ME,
                total: intensiveMinutesTotal,
              })}
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_MINUTES_AVG,
                total: collectiveAverage,
              })}
            </>
          )}
          {isUser && progressType === ProgressType.COLLECTIVE_CUSTOM && (
            <>
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_CUSTOM_ME,
                total: persistenceTotal,
                unit,
              })}
              {this.renderGoalGraph({
                parameter: GOAL_PARAMETER.COLLECTIVE_CUSTOM_AVG,
                total: collectiveAverage,
                unit,
              })}
            </>
          )}

          {progressType === ProgressType.COLLECTIVE_STEPS &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_STEPS,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              desktopOnly: true,
            })}
          {progressType === ProgressType.COLLECTIVE_CALORIES &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_CALORIES,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              desktopOnly: true,
              longWidth: !isUser,
            })}
          {progressType === ProgressType.COLLECTIVE_INTENSIVE_MINUTES &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_MINUTES,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              desktopOnly: true,
            })}
          {progressType === ProgressType.COLLECTIVE_CUSTOM &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.COLLECTIVE_CUSTOM,
              percentage: collectivePercentage + 0,
              progress: collectivePercentage + 0,
              total: collectiveTotal,
              goal: collectiveGoal,
              desktopOnly: true,
              unit,
            })}

          {isFormulaGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.INTENSIVE,
              percentage: intensiveMinutesPercentage,
              progress: intensiveMinutesProgress,
              total: intensiveMinutesTotal,
              goal: intensiveMinutesGoal,
              tooltip: t`Minutes above 65% of your maximum heart rate (based on age).`,
            })}
          {(isStepsGoal || isFormulaGoal) &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.STEPS,
              percentage: stepsPercentage,
              progress: stepsProgress,
              total: stepsTotal,
              goal: stepsGoal,
              tooltip: t`Number of steps from your device.`,
            })}
          {isFormulaGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.CALORIES,
              percentage: caloriesPercentage,
              progress: caloriesProgress,
              total: caloriesTotal,
              goal: caloriesGoal,
              tooltip: t`Calories goal is based on your age, sex, weight and height.`,
            })}
          {isFormulaGoal &&
            this.renderGoalGraph({
              parameter: GOAL_PARAMETER.FORMULA,
              percentage: formulaPercentage,
              progress: formulaProgress,
              total: 0,
              goal: 100,
              tooltip: t`The activity formula is based on three equally weighted parameters (minutes, steps, and calories).`,
            })}
        </div>
      )
    )
  }
}
